import { API_URL } from './config';

/**
 * Base URL for all API requests.
 * Sourced from REACT_APP_API_URL environment variable via config module.
 */
const API_BASE = API_URL;

/**
 * Makes an authenticated API request, attaching the Clerk session token
 * as a Bearer token when available.
 *
 * @param path - API path (e.g. '/villas' or '/bookings/123')
 * @param options - Standard RequestInit options (method, body, headers, etc.)
 * @returns Parsed JSON response
 * @throws Error with message from API error response or status text
 */
export async function apiRequest<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  // Get token from Clerk's global session (window.Clerk is available when ClerkProvider is mounted)
  const token = await (window as any).Clerk?.session?.getToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }

  return res.json();
}

/**
 * GET request with optional query parameters.
 *
 * @param path - API path (e.g. '/villas')
 * @param params - Optional query parameters as key-value pairs
 * @returns Parsed JSON response
 */
export async function apiGet<T = any>(
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): Promise<T> {
  let url = path;

  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    }
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  return apiRequest<T>(url, { method: 'GET' });
}

/**
 * POST request with a JSON body.
 *
 * @param path - API path (e.g. '/bookings')
 * @param body - Request body to be JSON-serialized
 * @returns Parsed JSON response
 */
export async function apiPost<T = any>(
  path: string,
  body: unknown
): Promise<T> {
  return apiRequest<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * PUT request with a JSON body.
 *
 * @param path - API path (e.g. '/villas/123')
 * @param body - Request body to be JSON-serialized
 * @returns Parsed JSON response
 */
export async function apiPut<T = any>(
  path: string,
  body: unknown
): Promise<T> {
  return apiRequest<T>(path, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * DELETE request.
 *
 * @param path - API path (e.g. '/bookings/123')
 * @returns Parsed JSON response (if any)
 */
export async function apiDelete<T = void>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: 'DELETE' });
}
